import {useUser} from '../auth/use-auth.tsx';
import {useCallback, useState} from 'react';
import {modals} from '@mantine/modals';
import {userManager} from '../auth';
import {
  Avatar,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Space,
  Stack,
  Text,
  ThemeIcon,
  Title,
  TransitionProps,
} from '@mantine/core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faArrowRightToArc,
  faUnlock,
} from '@fortawesome/pro-regular-svg-icons';
import {PrimaryColorCssVar} from '../styles/default-theme.tsx';
import {ChangePassword} from '../auth/change-password.tsx';
import classes from './user-menu.module.css';
import {ClassNames} from '@emotion/react';

export const UserMenu = () => {
  const user = useUser();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const onLogoutClick = useCallback(() => {
    return modals.openConfirmModal({
      title: 'Are you sure you want to sign out?',
      labels: {confirm: 'Log Out', cancel: 'Cancel'},
      onConfirm: async () => {
        await userManager.signoutRedirect();
      },
    });
  }, []);

  return (
    <ClassNames>
      {({cx}) => (
        <>
          <Menu width={220} position="bottom" transitionProps={transitionProps}>
            <Menu.Target>
              <Button
                className={classes.buttonTarget}
                variant={'outline'}
                leftSection={
                  <Avatar variant={'transparent'} radius="xl" size={25} />
                }
                rightSection={
                  <FontAwesomeIcon icon={faAngleDown} fontSize={10} />
                }
              >
                <Text fz={'14px'} fw={500} c={'black'}>
                  {user.name}
                </Text>
              </Button>
            </Menu.Target>

            <Menu.Dropdown p={0} style={dropdownStyle}>
              <Box
                h={50}
                bg={PrimaryColorCssVar}
                className={classes.menuCardHeader}
              />
              <Box pos={'absolute'} top={0} right={0} left={0} bottom={0} h={0}>
                <Group justify="center" pt={50 - 50 / 2} w={'100%'} h={0}>
                  <Avatar size={50} styles={avatarStyles}>
                    {user.name
                      .split(' ')
                      .map((x) => x.substring(0, 1).toUpperCase())
                      .join('')}
                  </Avatar>
                </Group>
              </Box>
              <Space h={25} />
              <Stack align="center" gap={0} justify={'center'}>
                <Text
                  className={cx(classes.textOverflow, classes.usernameText)}
                >
                  {user.name}
                </Text>
                <Text className={cx(classes.textOverflow, classes.emailText)}>
                  {user.email}
                </Text>
              </Stack>
              <Menu.Divider my={12} />

              <Menu.Label>Actions</Menu.Label>
              <Menu.Item
                onClick={() => setShowChangePassword(true)}
                leftSection={
                  <ThemeIcon variant={'transparent'}>
                    <FontAwesomeIcon icon={faUnlock} />
                  </ThemeIcon>
                }
              >
                Change Password
              </Menu.Item>
              <Menu.Item
                closeMenuOnClick={false}
                onClick={onLogoutClick}
                leftSection={
                  <ThemeIcon variant={'transparent'}>
                    <FontAwesomeIcon icon={faArrowRightToArc} />
                  </ThemeIcon>
                }
              >
                Log Out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Modal
            size="md"
            lockScroll={false}
            opened={showChangePassword}
            onClose={() => setShowChangePassword(false)}
            title={<Title order={4}>Change Password</Title>}
          >
            <ChangePassword onComplete={() => setShowChangePassword(false)} />
          </Modal>
        </>
      )}
    </ClassNames>
  );
};

const transitionProps: Partial<Omit<TransitionProps, 'mounted'>> = {
  transition: 'pop',
};

const avatarStyles = {
  root: {
    border: `3px solid ${PrimaryColorCssVar}`,
    background: 'white',
  },
};

const dropdownStyle = {
  boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
};